import { useEffect, useState } from "react";
import axios from "axios";
import {
  Sun, CloudSun, Cloud, CloudFog, CloudDrizzle, CloudRain,
  Snowflake, CloudLightning, Wind, Layers, ArrowUpRight, Video,
} from "lucide-react";
import { Reveal, Overline } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconFor = (code) => {
  if ([0, 1].includes(code)) return Sun;
  if (code === 2) return CloudSun;
  if (code === 3) return Cloud;
  if ([45, 48].includes(code)) return CloudFog;
  if ([51, 53, 55, 56, 57].includes(code)) return CloudDrizzle;
  if ([61, 63, 65, 66, 67, 80, 81, 82].includes(code)) return CloudRain;
  if ([71, 73, 75, 77, 85, 86].includes(code)) return Snowflake;
  if ([95, 96, 99].includes(code)) return CloudLightning;
  return Cloud;
};

const weekday = (d) => {
  try {
    return new Date(d).toLocaleDateString("en-NZ", { weekday: "short" });
  } catch {
    return d;
  }
};

const FieldCard = ({ f }) => {
  const Icon = f.current ? iconFor(f.current.weatherCode) : Cloud;
  const [embed, setEmbed] = useState(null);
  const [showCam, setShowCam] = useState(false);
  const [loadingCam, setLoadingCam] = useState(false);

  const openCam = async () => {
    if (embed) {
      setShowCam((v) => !v);
      return;
    }
    setLoadingCam(true);
    try {
      const res = await axios.get(`${API}/webcam/${f.key}`);
      if (res.data?.embedUrl) {
        setEmbed(res.data);
        setShowCam(true);
        return;
      }
      // no embed available — fall back to resort site
      if (f.webcam) window.open(f.webcam, "_blank", "noopener,noreferrer");
    } catch {
      if (f.webcam) window.open(f.webcam, "_blank", "noopener,noreferrer");
    } finally {
      setLoadingCam(false);
    }
  };

  return (
    <div
      data-testid={`snow-card-${f.key}`}
      className="bg-bg border border-white/10 p-7 md:p-8 flex flex-col"
    >
      <div className="flex items-start justify-between">
        <div>
          <h3 className="font-serif text-2xl md:text-3xl text-ink leading-tight">{f.name}</h3>
          <span className="font-sans text-[0.65rem] tracking-[0.2em] uppercase text-ash">
            {f.elevation} m
          </span>
        </div>
        <Icon className="h-9 w-9 text-gold shrink-0" strokeWidth={1.3} />
      </div>

      {f.current ? (
        <>
          <div className="mt-6 flex items-end gap-4">
            <span className="font-serif text-6xl text-ink leading-none">
              {f.current.temp}°
            </span>
            <span className="font-sans text-sm text-ash pb-2">{f.current.weatherText}</span>
          </div>

          <div className="mt-6 grid grid-cols-2 gap-4">
            <div className="flex items-center gap-3">
              <Layers className="h-5 w-5 text-gold" strokeWidth={1.4} />
              <div>
                <p className="font-sans text-lg text-ink leading-none">{f.current.snowDepthCm} cm</p>
                <p className="font-sans text-[0.6rem] tracking-[0.15em] uppercase text-faint mt-1">Snow depth</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Wind className="h-5 w-5 text-gold" strokeWidth={1.4} />
              <div>
                <p className="font-sans text-lg text-ink leading-none">{f.current.windKmh} km/h</p>
                <p className="font-sans text-[0.6rem] tracking-[0.15em] uppercase text-faint mt-1">Wind</p>
              </div>
            </div>
          </div>

          <div className="mt-7 pt-6 border-t border-white/10 grid grid-cols-4 gap-2">
            {f.daily.map((day, di) => {
              const DIcon = iconFor(day.weatherCode);
              const isToday = di === 0;
              return (
                <div
                  key={day.date}
                  className={`flex flex-col items-center text-center gap-1.5 rounded-md py-2 ${
                    isToday ? "bg-black/30 border border-gold/30" : ""
                  }`}
                >
                  <span className={`font-sans text-[0.6rem] tracking-[0.15em] uppercase ${isToday ? "text-metallic-gold" : "text-ash"}`}>
                    {isToday ? "Today" : weekday(day.date)}
                  </span>
                  <DIcon className={`h-5 w-5 ${isToday ? "text-gold" : "text-ink/70"}`} strokeWidth={1.4} />
                  <span className="font-sans text-xs text-gold">{day.snowfallCm}cm</span>
                  <span className="font-sans text-[0.65rem] text-faint">
                    {day.tmax}°/{day.tmin}°
                  </span>
                </div>
              );
            })}
          </div>
        </>
      ) : (
        <p className="mt-6 font-sans text-sm text-ash">Conditions temporarily unavailable.</p>
      )}

      {showCam && embed?.embedUrl && (
        <div className="mt-6 aspect-video w-full overflow-hidden border border-white/10" data-testid={`snow-embed-${f.key}`}>
          <iframe
            src={embed.embedUrl}
            title={embed.title || `${f.name} live webcam`}
            className="h-full w-full"
            allow="autoplay; fullscreen; picture-in-picture"
            loading="lazy"
          />
        </div>
      )}

      <div className="mt-7 flex items-center gap-6 flex-wrap">
        <a
          href={f.link}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`snow-link-${f.key}`}
          className="inline-flex items-center gap-2 font-sans text-[0.7rem] tracking-[0.15em] uppercase text-gold hover:text-ink transition-colors duration-300"
        >
          Full report <ArrowUpRight className="h-3.5 w-3.5" />
        </a>
        {f.webcam && (
          <button
            onClick={openCam}
            disabled={loadingCam}
            data-testid={`snow-webcam-${f.key}`}
            className="inline-flex items-center gap-2 font-sans text-[0.7rem] tracking-[0.15em] uppercase text-gold hover:text-ink transition-colors duration-300 disabled:opacity-60"
          >
            <Video className="h-3.5 w-3.5" strokeWidth={1.6} />
            {loadingCam ? "Loading…" : showCam ? "Hide webcam" : "Live webcam"}
          </button>
        )}
      </div>
    </div>
  );
};

export default function SnowReport() {
  const [data, setData] = useState(null);
  const [region, setRegion] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    axios
      .get(`${API}/snow-report`)
      .then((res) => {
        if (!active) return;
        setData(res.data);
        setRegion(res.data?.regions?.[0] || null);
      })
      .catch(() => active && setData(null))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, []);

  const updated = data?.updated
    ? new Date(data.updated * 1000).toLocaleString("en-NZ", {
        hour: "2-digit", minute: "2-digit", day: "numeric", month: "short", timeZone: "Pacific/Auckland",
      })
    : null;

  return (
    <section id="snow" className="relative bg-surface py-28 md:py-40 border-y border-white/10" data-testid="snow">
      <div className="mx-auto max-w-[1600px] px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div>
            <Reveal>
              <Overline>Live Conditions</Overline>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-6 font-serif font-light text-ink text-4xl md:text-6xl lg:text-7xl leading-[1]">
                Snow & weather <em className="italic text-metallic-gold">report</em>
              </h2>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-5 font-sans text-base text-ink/60 max-w-xl">
                Live conditions from the world’s finest ski fields.
              </p>
            </Reveal>
          </div>
          {updated && (
            <Reveal delay={0.15}>
              <span data-testid="snow-updated" className="font-sans text-xs tracking-[0.15em] uppercase text-ash">
                Updated {updated} NZT
              </span>
            </Reveal>
          )}
        </div>

        {loading ? (
          <p className="font-sans text-ash">Loading live conditions…</p>
        ) : data?.fields?.length ? (
          <>
            <div className="flex flex-wrap gap-3 md:gap-4 mb-14 md:mb-16" data-testid="snow-regions">
              {(data.regions || []).map((r) => (
                <button
                  key={r}
                  onClick={() => setRegion(r)}
                  data-testid={`snow-region-${r.replace(/\s+/g, "-").toLowerCase()}`}
                  className={`relative rounded-full border px-7 py-3 font-sans text-xs tracking-[0.2em] uppercase transition-all duration-500 ${
                    region === r
                      ? "border-gold text-metallic-gold bg-black/40"
                      : "border-white/15 text-ash hover:text-metallic-gold hover:border-gold/50"
                  }`}
                >
                  {r}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
              {data.fields
                .filter((f) => f.region === region)
                .map((f, i) => (
                  <Reveal key={f.key} delay={i * 0.06}>
                    <FieldCard f={f} />
                  </Reveal>
                ))}
            </div>
          </>
        ) : (
          <p className="font-sans text-ash">Live conditions are temporarily unavailable. Please check back shortly.</p>
        )}
      </div>
    </section>
  );
}
